import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_ME_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import { API_GET_EMPLOYEES_ME, KEY_GET_EMPLOYEES_ME } from '../constants';
import { MOCK_RESPONSE_GET_EMPLOYEES_ME } from '../mocks';

type RequestGetEmployeesMe = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_ME_PATH_KEY
>['request']['query'];

type ResponseGetEmployeesMe = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_ME_PATH_KEY
>['response']['body'];

export type CallParamsGetEmployeesMe = RequestGetEmployeesMe & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetEmployeesMe = ResponseGetEmployeesMe;

type CallErrorGetEmployeesMe = {
  message: string;
  code: number;
};

export type CallSelectorsGetEmployeesMe = {
  hasTeamLeader: boolean;
  hasManagerLeader: boolean;
  leadership: ResponseGetEmployeesMe['leadership'] | null;
  fullName: string | null;
  shortName: string | null;
  role: string | null | undefined;
  email: string | null | undefined;
};

export const getEmployeesMeCall = createGeneralCall<
  CallParamsGetEmployeesMe,
  CallResponseGetEmployeesMe,
  CallErrorGetEmployeesMe,
  CallSelectorsGetEmployeesMe
>({
  id: KEY_GET_EMPLOYEES_ME,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return `${API_GET_EMPLOYEES_ME(org as unknown as string)}?expand=leadership`;
  },
  paramsSelector:
    ({ payload }) =>
    () => {
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_EMPLOYEES_ME : undefined,
  selectors: {
    hasTeamLeader: ({ selectors }) =>
      createSelector(selectors.response, res => res?.leadership?.team !== undefined || false),
    hasManagerLeader: ({ selectors }) =>
      createSelector(selectors.response, res => res?.leadership?.department !== undefined || false),
    leadership: ({ selectors }) =>
      createSelector(selectors.response, res => res?.leadership || null),
    fullName: ({ selectors }) =>
      createSelector(selectors.response, res => (res ? `${res.firstName} ${res.lastName}` : null)),
    shortName: ({ selectors }) =>
      createSelector(selectors.response, res =>
        res && res.firstName && res.lastName
          ? `${res.firstName.charAt(0)}${res.lastName.charAt(0)}`
          : null,
      ),
    role: ({ selectors }) => createSelector(selectors.response, res => (res ? res.role : null)),
    email: ({ selectors }) => createSelector(selectors.response, res => (res ? res.email : null)),
  },
});
